import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Alert,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { 
  Eye, 
  EyeOff, 
  User, 
  Mail, 
  Building2, 
  UserCheck, 
  Lock,
  Briefcase,
  CheckCircle,
  X,
} from 'lucide-react';
import { API_URL } from "../globals";
import { supabase } from "../supabaseClient";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Validation states
  const [isValidating, setIsValidating] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [validationError, setValidationError] = useState('');
  
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [position, setPosition] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Submission states
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      validateInvitation();
    } else {
      setValidationError('No invitation token provided');
      setIsValidating(false);
    }
  }, [token]);

  const validateInvitation = async () => {
    try {
      setIsValidating(true);
      setValidationError('');
      
      const response = await axios.get(`${API_URL}/validate-invitation/${token}`);
      
      if (response.data) {
        setInvitation(response.data);
      }
    } catch (error) {
      console.error('Error validating invitation:', error);
      const errorMessage = error.response?.data?.error || 'Failed to validate invitation';
      setValidationError(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!firstName.trim() || !lastName.trim() || !password) {
      setAcceptError('Please fill in all required fields');
      return;
    }
    
    if (password.length < 6) {
      setAcceptError('Password must be at least 6 characters long');
      return;
    }

    setIsAccepting(true);
    setAcceptError('');

    try {
      const response = await axios.post(`${API_URL}/accept-invitation`, {
        token,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
        position: position.trim(),
      });

      if (response.status === 200) {
        setIsSuccess(true);
        // Redirect to sign in after a delay
        setTimeout(() => {
          navigate('/signin', { 
            state: { 
              message: 'Account created successfully! Please sign in with your credentials.',
              email: invitation.email 
            }
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      const errorMessage = error.response?.data?.error || 'Failed to accept invitation';
      setAcceptError(errorMessage);
    } finally {
      setIsAccepting(false);
    }
  };

  if (isValidating) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#121212',
        flexDirection: 'column',
        gap: 2,
      }}>
        <CircularProgress size={40} sx={{ color: '#fff' }} />
        <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
          Validating invitation...
        </Typography>
      </Box>
    );
  }

  if (validationError) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#121212',
        px: 2,
      }}>
        <Paper sx={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #2a2a2a',
          borderRadius: '12px',
          p: 4,
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
        }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              width: '64px', 
              height: '64px', 
              borderRadius: '50%', 
              backgroundColor: '#d32f2f1a', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}>
              <X size={32} color="#f44336" />
            </Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
              Invalid Invitation
            </Typography>
            <Typography sx={{ color: '#888', fontSize: '0.875rem', lineHeight: 1.6 }}>
              {validationError}
            </Typography>
          </Box>
          <Button
            onClick={() => navigate('/signin')}
            sx={{
              backgroundColor: '#fff',
              color: '#121212',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            Go to Sign In
          </Button>
        </Paper>
      </Box>
    );
  }

  if (isSuccess) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#121212',
        px: 2,
      }}>
        <Paper sx={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #2a2a2a',
          borderRadius: '12px',
          p: 4,
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
        }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              width: '64px', 
              height: '64px', 
              borderRadius: '50%', 
              backgroundColor: '#1b5e201a', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}>
              <CheckCircle size={32} color="#4caf50" />
            </Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
              Welcome to the team!
            </Typography>
            <Typography sx={{ color: '#888', fontSize: '0.875rem', lineHeight: 1.6 }}>
              Your account has been created successfully. Redirecting to sign in...
            </Typography>
          </Box>
          <CircularProgress size={24} sx={{ color: '#fff' }} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#121212',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: 4,
    }}>
      <Paper sx={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '12px',
        p: 4,
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ 
            color: '#fff', 
            fontWeight: 600, 
            mb: 1,
            fontSize: '1.75rem',
          }}>
            Join Your Team
          </Typography>
          <Typography sx={{ 
            color: '#888', 
            fontSize: '0.875rem',
            lineHeight: 1.6,
          }}>
            Complete your profile to accept the invitation
          </Typography>
        </Box>

        {/* Invitation Details */}
        <Box sx={{
          backgroundColor: '#252525',
          border: '1px solid #333',
          borderRadius: '8px',
          p: 3,
          mb: 4,
        }}>
          <Typography sx={{ 
            color: '#888', 
            fontSize: '0.75rem', 
            mb: 2,
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: '0.05em'
          }}>
            Invitation Details
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Mail size={16} color="#888" />
              <Box>
                <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Email</Typography>
                <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                  {invitation.email}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Building2 size={16} color="#888" />
              <Box>
                <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Company</Typography>
                <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                  {invitation.company_name}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <User size={16} color="#888" />
              <Box>
                <Typography sx={{ color: '#888', fontSize: '0.75rem' }}>Invited by</Typography>
                <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                  {invitation.invited_by_name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Registration Form */}
        <Box component="form" onSubmit={handleAcceptInvitation}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Name Fields */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                variant="outlined"
                size="small"
                required
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#252525',
                    color: '#fff',
                    '& fieldset': {
                      borderColor: '#333',
                    },
                    '&:hover fieldset': {
                      borderColor: '#555',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#fff',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#888',
                    '&.Mui-focused': {
                      color: '#fff',
                    },
                  },
                }}
              />
              
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                variant="outlined"
                size="small"
                required
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#252525',
                    color: '#fff',
                    '& fieldset': {
                      borderColor: '#333',
                    },
                    '&:hover fieldset': {
                      borderColor: '#555',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#fff',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#888',
                    '&.Mui-focused': {
                      color: '#fff',
                    },
                  },
                }}
              />
            </Box>

            {/* Position Field */}
            <TextField
              label="Position (Optional)"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Briefcase size={16} color="#888" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#252525',
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#333',
                  },
                  '&:hover fieldset': {
                    borderColor: '#555',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#fff',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#888',
                  '&.Mui-focused': {
                    color: '#fff',
                  },
                },
              }}
            />

            {/* Password Field */}
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              size="small"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock size={16} color="#888" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#888' }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#252525',
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#333',
                  },
                  '&:hover fieldset': {
                    borderColor: '#555',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#fff',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#888',
                  '&.Mui-focused': {
                    color: '#fff',
                  },
                },
              }}
            />

            {/* Error Message */}
            {acceptError && (
              <Alert 
                severity="error" 
                sx={{ 
                  backgroundColor: '#d32f2f1a',
                  color: '#f44336',
                  border: '1px solid #f4433633',
                  '& .MuiAlert-icon': { color: '#f44336' }
                }}
              >
                {acceptError}
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              disabled={isAccepting}
              startIcon={
                isAccepting ? (
                  <CircularProgress size={16} sx={{ color: '#121212' }} />
                ) : (
                  <UserCheck size={16} />
                )
              }
              sx={{
                backgroundColor: '#fff',
                color: '#121212',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                py: 1.5,
                mt: 1,
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#333',
                  color: '#666',
                },
              }}
            >
              {isAccepting ? 'Creating Account...' : 'Accept Invitation & Create Account'}
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Divider sx={{ borderColor: '#2a2a2a', my: 3 }} />
        <Typography sx={{ 
          color: '#666', 
          fontSize: '0.75rem',
          textAlign: 'center',
          lineHeight: 1.6,
        }}>
          By accepting this invitation, you agree to join {invitation.company_name} and 
          will have access to team resources with {invitation.role.toLowerCase()} permissions.
        </Typography>
      </Paper>
    </Box>
  );
};

export default AcceptInvitation; 